import { IMeta } from '@models/IMeta';
import { IVideo } from '@models/IVideo';
import useMediaQuery from '@utility/hooks/useMediaQuery';
import { motion, useScroll, useSpring, useTransform } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  readonly backgroundColour: string;
  readonly video: IVideo;
  readonly mobileVideo?: IVideo;
  readonly meta?: IMeta;
}

const ExpandingVideo = ({
  backgroundColour,
  mobileVideo,
  video,
  meta,
}: Props) => {
  const isMobile = useMediaQuery(752);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [inView, setInView] = useState(false);
  const isPreview = meta?.editMode || meta?.previewMode;

  const { scrollYProgress } = useScroll({
    target: videoContainerRef,
    offset: ['-100% end', 'end end'],
  });

  const smoothScrollProgress = useSpring(scrollYProgress, {
    stiffness: 1000,
    damping: 80,
    mass: 1,
  });

  const scale = useTransform(smoothScrollProgress, [0.5, 1], [0.8, 1]);

  const videoSource = useMemo(
    () =>
      isMobile && mobileVideo?.videoSource
        ? mobileVideo?.videoSource
        : video.videoSource,
    [isMobile, mobileVideo?.videoSource, video.videoSource],
  );

  const handleScroll = useCallback(() => {
    setHasScrolled(true);
  }, []);

  useEffect(() => {
    let observer: IntersectionObserver;

    if (videoContainerRef.current) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry) setInView(entry.isIntersecting);
        },
        { threshold: 0.3 },
      );

      observer.observe(videoContainerRef.current);

      return () => {
        if (observer) {
          observer.unobserve(videoContainerRef.current!);
        }
      };
    }
  }, []);

  useEffect(() => {
    const handleScrollEvent = () => {
      if (!hasScrolled) {
        requestAnimationFrame(handleScroll);
      }
    };

    window.addEventListener('scroll', handleScrollEvent);

    return () => {
      window.removeEventListener('scroll', handleScrollEvent);
    };
  }, [hasScrolled, handleScroll]);

  useEffect(() => {
    if (videoRef.current) {
      if (inView && hasScrolled) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [inView, hasScrolled]);

  return (
    <section
      style={{
        backgroundColor: backgroundColour ? backgroundColour : undefined,
      }}
      data-background-color={backgroundColour}
    >
      <div>
        <motion.div
          ref={videoContainerRef}
          onFocus={() => setIsHovered(true)}
          onBlur={() => setIsHovered(false)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <motion.div style={{ scale }}>
            <div
              className={isPreview ? 'tw-h-[600px]' : 'tw-h-[calc(100vh-60px)]'}
            >
              <div className="tw-inset-0 tw-h-full tw-w-full">
                <video
                  ref={videoRef}
                  preload="metadata"
                  loop
                  muted
                  controls={isHovered}
                  playsInline
                  className="tw-h-full tw-w-full tw-object-cover tw-object-center"
                >
                  <source src={`${videoSource}#t=0.1`} type="video/mp4" />
                </video>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ExpandingVideo;
